import styled from 'styled-components';
import { Form, FormInstance } from 'antd';
import { useForm, useWatch } from 'antd/es/form/Form';
import { useCallback, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { GradientButton } from 'components';
import { BASE_GENERIC_ROUTE, BESTFIT_NONE, BUILDER_URL, MOBILE_WIDTH, ONBOARDING_FORM_WIDTH } from 'const';
import { includesNumber, includesSpecialCharacter, validateEmail } from 'utils';
import { OnboardingAnswers, refreshToken, submitOnboarding, validateEmailOnServer } from 'api';
import { PRIMARY_COLOUR, PRIMARY_TEXT_COLOUR, SECONDARY_TEXT_COLOUR } from 'theme';

import {
  ALTERNATE_QUESTION_NAMES,
  OnboardingQuestions,
  QUESTION_NAMES,
  OnboardingSignup,
  SIGNUP_FIELDS,
} from './components';

const FormBody = styled.div`
  width: ${ONBOARDING_FORM_WIDTH};
  min-width: 360px;
  height: fit-content;
  min-height: 764px;
  border-radius: 20px;
  background-color: #ffffff;
  overflow: visible;

  padding: 60px;
  @media (max-width: ${MOBILE_WIDTH}) {
    padding-left: 32px;
    padding-right: 32px;
  }

  @media (max-height: 850px) {
    @media (max-width: ${MOBILE_WIDTH}) {
      min-height: 600px;
    }
  }
`;
const Heading = styled.div`
  color: ${PRIMARY_TEXT_COLOUR};
  font-weight: 600;
  font-size: 30px;
  line-height: 36px;
`;
const Subheading = styled.div`
  margin-top: 12px;

  color: ${SECONDARY_TEXT_COLOUR};
  font-weight: 400;
  font-size: 20px;
  line-height: 24px;
`;

const Caption = styled.div`
  margin-top: 30px;
  font-size: 12px;
  line-height: 14px;
  font-weight: 500;
  color: ${SECONDARY_TEXT_COLOUR};
  text-align: center;
`;

const Link = styled.a`
  color: ${PRIMARY_COLOUR};
  text-decoration: underline;
`;

const isFormFilled = (form: FormInstance, continued: boolean, hasMembershipSite: boolean) => {
  const values = form.getFieldsValue();
  const fieldsToCheck = !continued ? SIGNUP_FIELDS : hasMembershipSite ? QUESTION_NAMES : ALTERNATE_QUESTION_NAMES;
  // Check all fields on the current tab are populated
  for (const fieldName of fieldsToCheck) {
    const value = values[fieldName];
    if (!value) {
      // Field is empty
      return false;
    }
    if (Array.isArray(value) && value.length === 0) {
      // Ensure any multiselect values have a value in the array
      return false;
    }
  }
  // All fields are populated
  return true;
};

export const OnboardingForm = () => {
  const [form] = useForm<OnboardingAnswers>();
  const [continued, setContinued] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const bestFit = useWatch('BestFit', form);

  const hasMembershipSite = useMemo(() => {
    return !(bestFit && bestFit.includes(BESTFIT_NONE));
  }, [bestFit]);

  const handleContinue = useCallback(async () => {
    const values = form.getFieldsValue();

    const email = values.Email;
    if (!validateEmail(email)) {
      form.setFields([
        {
          name: 'Email',
          errors: ['Must be a valid email address'],
        },
      ]);
      return;
    } else {
      form.setFields([{ name: 'Email', errors: [] }]);
    }

    const validateResponse = await validateEmailOnServer(email);
    if (validateResponse.status !== 200) {
      form.setFields([
        {
          name: 'Email',
          errors: [validateResponse.data?.detail || 'Email is unavailable for use'],
        },
      ]);
      return;
    } else {
      form.setFields([{ name: 'Email', errors: [] }]);
    }

    const password = values.Password;
    if (!(password.length >= 8 && includesNumber(password) && includesSpecialCharacter(password))) {
      form.setFields([
        {
          name: 'Password',
          errors: ['Minimum 8 characters, 1 number, 1 special character'],
        },
      ]);
      return;
    } else {
      form.setFields([{ name: 'Password', errors: [] }]);
    }

    setContinued(true);
    setButtonDisabled(true);
  }, [form, setContinued, setButtonDisabled]);

  const handleSubmit = useCallback(async () => {
    const values = form.getFieldsValue();

    setLoading(true);

    // Submit the app/lead to the builder API
    // VimeoOTT, Kajabi apps will create an app
    // Other apps will save the lead
    const createApp = ['Kajabi', 'VHX'].includes(values.DataSource ?? 'none');

    if (window.Rewardful?.affiliate.token) {
      values.RewardfulId = window.Rewardful.affiliate.token;
    }
    const response = await submitOnboarding(values, createApp, hasMembershipSite);
    setLoading(false);

    if (response.status === 200 || response.status === 201) {
      if (createApp) {
        // The user should now be able to login to the builder
        if (window.innerWidth < 600) {
          // Indicate they should use a desktop device
          navigate(`${BASE_GENERIC_ROUTE}/mobile`, { state: values });
        } else {
          const { AppId, Email, Name, access_token } = response.data || {};
          localStorage.setItem('user_email', Email);
          localStorage.setItem('user_name', Name);
          localStorage.setItem('appBeingEdited', AppId);
          localStorage.setItem('jwt', access_token);
          await refreshToken(); // Use the refresh token cookie to save it to the browser
          navigate(`${BASE_GENERIC_ROUTE}/success`);
        }
      } else {
        // Indicate they will need to wait for VidApp to proceed
        navigate(`${BASE_GENERIC_ROUTE}/done`, { state: values });
      }
    } else {
      let error = 'Oops, there was an error creating your account';
      if (response.status === 400 && response.data) {
        error = response.data.detail;
      }
      form.setFields([
        {
          name: 'Confirmed',
          errors: [error],
        },
      ]);
    }
  }, [form, navigate, hasMembershipSite]);

  return (
    <FormBody>
      <Heading>Let's get started!</Heading>
      <Subheading>
        {'Create your account to start building your app.'}
        <br />
        {' No credit card required.'}
      </Subheading>
      <Form
        form={form}
        onValuesChange={(e) => {
          setButtonDisabled(!isFormFilled(form, continued, hasMembershipSite));
        }}
      >
        <OnboardingSignup visible={!continued} />
        <OnboardingQuestions visible={continued} hasMembershipSite={hasMembershipSite} />
      </Form>
      <GradientButton
        onClick={async () => {
          if (!continued) {
            handleContinue();
          } else {
            // Submit creation of this new app and user
            await handleSubmit();
          }
        }}
        loading={loading}
        disabled={buttonDisabled}
      >
        {continued ? 'Create Account' : 'Continue'}
      </GradientButton>
      <Caption>
        {'Already have an account? '}
        <Link href={BUILDER_URL}>Sign In</Link>
      </Caption>
    </FormBody>
  );
};
